import { useEffect, useState } from 'react'
import { APP_VERSION } from '../lib/version'

/**
 * Detects the stale-tab scenario: the SPA keeps running whatever bundle it loaded, so a
 * user with a long-lived tab can be minutes-to-days behind the deployed frontend without
 * any way to know. index.html being no-cache only helps on the *next* full load.
 *
 * The Docker build writes the same version it bakes into the bundle to /version.json,
 * which nginx serves no-cache. This hook re-reads it when the tab regains focus (the
 * moment people come back to stale tabs) and on a slow interval, and reports staleness
 * so the shell can show a refresh banner.
 *
 * Deliberately fail-quiet: under `npm run dev` there is no version.json (Vite 404s or
 * answers with index.html), and a fetch hiccup must never nag the user — only a
 * successfully parsed, genuinely different version counts.
 */
const CHECK_EVERY_MS = 15 * 60_000

export function useVersionCheck(): boolean {
  const [stale, setStale] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function check() {
      try {
        const resp = await fetch('/version.json', { cache: 'no-store' })
        if (!resp.ok) return
        const { version } = (await resp.json()) as { version?: string }
        if (!cancelled && !!version && version !== APP_VERSION) setStale(true)
      } catch {
        /* network blip or non-JSON dev response — say nothing */
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') void check()
    }

    void check()
    const timer = setInterval(() => void check(), CHECK_EVERY_MS)
    window.addEventListener('focus', onVisible)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('focus', onVisible)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return stale
}
